<script lang="ts">
	import type { EmblaCarouselType, EmblaOptionsType, EmblaPluginType } from "embla-carousel";
	import emblaCarouselSvelte from "embla-carousel-svelte";
	import { onDestroy } from "svelte";
	import { lerp } from "../../../core/utils/lerp.js";

	type Item = $$Generic;

	export let api: EmblaCarouselType | undefined = undefined;
	export let canNext = false;
	export let canPrev = false;
	let className: string | undefined = undefined;
	export { className as class };
	export let items: Item[];
	export let itemsContainerClass: string | undefined = undefined;
	export let options: EmblaOptionsType = {};
	export let plugins: EmblaPluginType[] = [];
	export let type: "flex" | "grid" = "flex";

	let duplicateLoopTimes = 1;

	function onemblaInit(event: CustomEvent<EmblaCarouselType>): void {
		api = event.detail;
		const biggestSlide = api
			.slideNodes()
			.reduceRight((prev, curr) => (curr.clientWidth > prev.clientWidth ? curr : prev));
		const possibleSlideAmount = Math.ceil(api.containerNode().clientWidth / biggestSlide.clientWidth);
		duplicateLoopTimes = Math.ceil(
			items.length <= possibleSlideAmount ? lerp(possibleSlideAmount + 1, 2, items.length / possibleSlideAmount) : 1,
		);
	}

	function updateButtons(emblaApi: EmblaCarouselType): void {
		canNext = emblaApi.canScrollNext();
		canPrev = emblaApi.canScrollPrev();
	}

	let cleanup: (() => void) | undefined;

	$: {
		cleanup?.();
		cleanup = undefined;

		const apiInstance = api;

		if (apiInstance) {
			const listener = updateButtons.bind(null, apiInstance);

			listener();
			apiInstance.on("resize", listener);
			apiInstance.on("reInit", listener);
			apiInstance.on("init", listener);
			apiInstance.on("select", listener);

			cleanup = (): void => {
				apiInstance.off("resize", listener);
				apiInstance.off("reInit", listener);
				apiInstance.off("init", listener);
				apiInstance.off("select", listener);
			};
		} else {
			canNext = false;
			canPrev = false;
		}
	}

	onDestroy(() => {
		cleanup?.();
	});

	function repeatItems(source: Item[], times: number): Item[] {
		const result: Item[] = [];
		for (let i = 0; i < times; i++) {
			// eslint-disable-next-line @typescript-eslint/no-unsafe-argument
			result.push(...source);
		}
		return result;
	}

	$: duplicatedItems = options.loop ? repeatItems(items, duplicateLoopTimes) : items;
</script>

<div
	class="overflow-hidden {items.length > 1 ? 'cursor-grab active:cursor-grabbing' : ''} {className ?? ''}"
	use:emblaCarouselSvelte={{
		options: {
			duration: 20,
			inViewThreshold: 0.5,
			watchDrag: items.length > 1,
			...options,
		},
		plugins,
	}}
	on:emblaInit={onemblaInit}
>
	<div
		class="h-full min-h-[inherit] [&>*]:min-w-0 [&>*]:max-w-full [&>*]:shrink-0 [&>*]:grow-0 {itemsContainerClass ??
			''} {type}"
		class:flex-col={type === "flex" && options.axis === "y"}
	>
		{#each duplicatedItems as it, index (index)}
			<slot item={it} {index} isDuplicate={index >= items.length} />
		{/each}
	</div>
</div>
